import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import useOrder from 'modules/order/state/order/use-order'
import ScrollableList from 'modules/ui/molecules/ScrollableList'
import useDesignationSearch from 'modules/order/components/designation/use-designation-search'
import DrawerPanel from 'modules/drawer/components/drawer-panel/drawer-panel'
import useDrawer from 'modules/drawer/state/use-drawer'
import useConfig from 'modules/config/state/use-config'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { focusOnElement } from 'modules/drawer/components/drawer/drawer.utils'
import { ORDER_DESIGNATION_DRAWER_OPENER_DATA_ID } from './order-designation-drawer-opener'

const OrderDesignationDrawerPanel = () => {
  const { t } = useTranslation()
  const config = useConfig()
  const drawer = useDrawer({ onClose: focusOnElement(ORDER_DESIGNATION_DRAWER_OPENER_DATA_ID) })
  const order = useOrder()

  const isContentPanelNull = config?.contentPanel === null

  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)

  const [selectedDesignationId, setSelectedDesignationId] = useState(
    order.designation.value.designationId,
  )

  const { data, error, hasMore, loading, designations } = useDesignationSearch(
    config.campaignId,
    query,
    pageNumber,
  )

  // Config and Order don't have the correct default designation ID,
  // so we get it from the designation list instead
  useEffect(() => {
    const defaultDesignationId = designations?.find((d) => d.isDefaultDesignation)?.id

    if (defaultDesignationId && !selectedDesignationId) {
      setSelectedDesignationId(defaultDesignationId)
    }
  }, [designations])

  const onSelect = ({ value: designationId }: { value: number }) => {
    const designation = designations?.find((d) => d.id === designationId)

    if (designation) {
      setSelectedDesignationId(designationId)
    }
  }

  const onSave = () => {
    const designation = designations?.find((d) => d.id === selectedDesignationId)

    if (designation) {
      order.designation.setValue({
        designationId: designation.id,
        designationName: designation.name,
      })

      drawer.close()
    }
  }

  const onCancel = () => {
    drawer.close()
  }

  const onFetch = (query: string, pageNumber: number) => {
    setQuery(query)
    setPageNumber(pageNumber)
  }

  if (!drawer.current) {
    return null
  }

  return (
    <DrawerPanel title={drawer.current.label} closeButtonDataId="designation-drawer-close-button">
      <DrawerPanel.Body>
        <ScrollableList
          data={data}
          error={error}
          loading={loading}
          hasMore={hasMore}
          onSelect={onSelect}
          fetchNewData={onFetch}
          selected={selectedDesignationId}
          dataId="designation-drawer"
          autoFocus
        />
      </DrawerPanel.Body>
      <DrawerPanel.Footer isMobileView={isContentPanelNull}>
        <DrawerPanel.FooterAction
          onClick={onCancel}
          data-id="designation-drawer-cancel-button"
          isMobileView={isContentPanelNull}
        >
          {t('orderDesignationDrawerPanel.footerAction.cancel', 'Cancel')}
        </DrawerPanel.FooterAction>
        <DrawerPanel.FooterAction
          onClick={onSave}
          data-id="designation-drawer-save-button"
          color={DEFAULT_THEME_COLORS['--theme-color--neutral-20']}
          isMobileView={isContentPanelNull}
        >
          {t('orderDesignationDrawerPanel.footerAction.save', 'Save')}
        </DrawerPanel.FooterAction>
      </DrawerPanel.Footer>
    </DrawerPanel>
  )
}

export default OrderDesignationDrawerPanel
